import { useEffect, useState } from 'react';
import { TableData, TableHeader } from '../../../../components/Table/DataTable';
import PlotlyGraphs from '../../../../components/Data/PlotlyGraphs';
import { calculateConformalMode, getConformalValue, isConformalMethodLabel } from '../../../../utils/formattingFns';
import { conformalStatColors } from '../../../../utils/formattingFns';

const {
    outOfDomainColor,
    inconclusiveColor,
    inactiveColor,
    activeColor,
} = conformalStatColors;


const legendItems = [
    {
        label: 'Active',
        color: activeColor,
    },
    {
        label: 'Inactive',
        color: inactiveColor,
    },
    {
        label: 'Inconclusive',
        color: inconclusiveColor,
    },
    {
        label: 'Out of Domain',
        color: outOfDomainColor,
    }
];

export default function FrequencyVsAlphaConsensus({
    data, 
    threshold,
    headers,
}: {
    data: TableData[],
    threshold: number,
    headers: TableHeader[],
}) {
    const [ plotData, setPlotData ] = useState<Plotly.Data[]>([]);
    const [ visibility, setVisibility ] = useState<boolean[]>(Array.from(Array(4).keys()).map(key => true));

    const toggleTrace = (index: number) => {
        const newVisibility = [...visibility];
        newVisibility[index] = !newVisibility[index];
        setVisibility(newVisibility);
    };

    const formatStackedData = (records: TableData[], threshold: number, visibility: boolean[]) => {
        if (records.length > 0) {
            let methodNames: string[] = [];
            records[0].fields.forEach(field => {
                const matchingHeader = headers.find(header => header.value === field.associatedColumn);
                if (matchingHeader && isConformalMethodLabel(matchingHeader.label)) {
                    methodNames.push(matchingHeader.label)
                }
            });

            const sortedMethods = methodNames.sort((a,b) => {
                if (a.toLocaleLowerCase() < b.toLocaleLowerCase()) {
                    return -1;
                } else if (a.toLocaleLowerCase() > b.toLocaleLowerCase()) {
                    return 1;
                } else {
                    return 0;
                }
            });
            const methods = ['mode'].concat(sortedMethods).flat();

            if (methods.length > 0) {
                
                let methodValues: {method: string, values: {
                    OOD: number,
                    INC: number,
                    INA: number,
                    ACT: number,
                }}[] = [];

                methods.forEach(method => {
                    let OOD = 0;
                    let INC = 0;
                    let INA = 0;
                    let ACT = 0;
                    if (method !== 'mode') {
                        records.forEach(record => {
                            const matchingHeader = headers.find(header => header.label === method);
                            if (matchingHeader) {
                                const matchingMethod = record.fields.find(field => field.associatedColumn === matchingHeader.value);
                                if (matchingMethod) {
                                    const value = matchingMethod.value as number[];
                                    const { label } = getConformalValue(value, threshold);
                                    if (label === 'Out of Domain') {
                                        OOD += 1;
                                    } else if (label === 'Inconclusive') {
                                        INC += 1;
                                    } else if (label === 'Inactive') {
                                        INA += 1;
                                    } else if (label === 'Active') {
                                        ACT += 1;
                                    }
                                }
                            }
                        });
                    } else {
                        records.forEach(record => {
                            const { label } = calculateConformalMode(record.fields, headers, threshold);
                            if (label === 'Out of Domain') {
                                OOD += 1;
                            } else if (label === 'Inconclusive') {
                                INC += 1;
                            } else if (label === 'Inactive') {
                                INA += 1;
                            } else if (label === 'Active') {
                                ACT += 1;
                            }
                        });
                    }

                    methodValues.push({method, values: { OOD, INC, INA, ACT }});
                });

                const traceOne: any = {
                    x: methodValues.map(val => val.values.ACT),
                    y: methodValues.map(val => val.method),
                    text: methodValues.map(val => val.values.ACT),
                    textposition: 'inside',
                    insidetextanchor: 'start',
                    name: 'Active',
                    orientation: 'h',
                    marker: {
                        color: activeColor,
                        width: 1,
                    },
                    type: 'bar',
                    visible: visibility[0] ? true : 'legendonly',
                }

                const traceTwo: any = {
                    x: methodValues.map(val => val.values.INA),
                    y: methodValues.map(val => val.method),
                    text: methodValues.map(val => val.values.INA),
                    textposition: 'inside',
                    insidetextanchor: 'start',
                    name: 'Inactive',
                    orientation: 'h',
                    marker: {
                        color: inactiveColor,
                        width: 1,
                    },
                    type: 'bar',
                    visible: visibility[1] ? true : 'legendonly',
                }

                const traceThree: any = {
                    x: methodValues.map(val => val.values.INC),
                    y: methodValues.map(val => val.method),
                    text: methodValues.map(val => val.values.INC),
                    textposition: 'inside',
                    insidetextanchor: 'start',
                    name: 'Inconclusive',
                    orientation: 'h',
                    marker: {
                        color: inconclusiveColor,
                        width: 1,
                    },
                    type: 'bar',
                    visible: visibility[2] ? true : 'legendonly',
                }

                const traceFour: any = {
                    x: methodValues.map(val => val.values.OOD),
                    y: methodValues.map(val => val.method),
                    text: methodValues.map(val => val.values.OOD),
                    textposition: 'inside',
                    insidetextanchor: 'start',
                    name: 'Out of Domain',
                    orientation: 'h',
                    marker: {
                        color: outOfDomainColor,
                        width: 1,
                    },
                    type: 'bar',
                    visible: visibility[3] ? true : 'legendonly',
                }

                setPlotData([traceOne, traceTwo, traceThree, traceFour]);
                
            }
        }
    }

    useEffect(() => {
        if (threshold !== null && threshold !== undefined && data.length > 0) {
            formatStackedData(data, threshold, visibility);
        }

        //eslint-disable-next-line
    }, [threshold, data, visibility, headers]);


    return (
        <div className="w-full p-6 flex justify-between items-start gap-6">
            <div className='grow'>
                <PlotlyGraphs data={plotData} type="stack" isSquare={false} threshold={threshold}/>
            </div>
            <div className='flex flex-col justify-start items-start gap-6 mt-5'>
                <div className='flex flex-col justify-start items-start gap-2'>
                    {legendItems.map((legend,i) => (
                        <button onClick={() => toggleTrace(i)} className={`flex justify-start items-center gap-3 ${!visibility[i] && 'opacity-20'} hover:brightness-90 transition-all`}>
                            <div style={{ backgroundColor: legend.color }} className={`w-[14px] rounded-sm aspect-square`}/>
                            <p className='text-default text-nowrap text-[14px] font-medium'>{legend.label}</p>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    )
}